import { QuestionFactory } from 'survey-core';
import { Question } from 'survey-react';
import {
  SurveyPDF,
  IDocOptions,
  SurveyHelper,
  DrawCanvas,
  HorizontalAlign,
  VerticalAlign,
  DocController,
} from 'survey-pdf';

import { getPersonBirthday } from 'shared/helpers/makeDate';

import { Questionnaire as QuestionnaireInterface } from 'shared/interfaces';
import { SurveyPDFType, ICustomPdfBrick } from './DownloadPDF.types';

import { AmiriBase64 } from 'assets/theme/fertilly/fonts/AmiriBase64';

DocController.addFont('myAmiri', AmiriBase64.thin, 'normal');
DocController.addFont('myAmiri', AmiriBase64.medium, 'bold');
DocController.addFont('myAmiri', AmiriBase64.lightItalic, 'italic');
DocController.addFont('myAmiri', AmiriBase64.blackItalic, 'bolditalic');

const exportToPdfOptions: IDocOptions = {
  haveCommercialLicense: true,
  fontSize: 12,
  fontName: 'myAmiri',
  margins: { top: 30, left: 10, right: 10, bot: 10 },
  applyImageFit: true,
};

export async function savePdf(
  surveyJSONSchema: QuestionnaireInterface,
  surveyData: object,
  locale: string,
  type: SurveyPDFType,
  nameTitle: string,
  name: string,
  birthDateTitle: string,
  birthDate: string,
  logoBase64: string | null,
  returnAsFile: boolean
) {
  const isLayout = type === 'layout';

  SurveyHelper.FORM_BORDER_VISIBLE = isLayout;

  const surveyPdf = new SurveyPDF(surveyJSONSchema, exportToPdfOptions);

  surveyPdf.mode = isLayout ? 'edit' : 'display';
  surveyPdf.locale = locale;
  surveyPdf.data = surveyData;
  surveyPdf.showInvisibleElements = isLayout;

  surveyPdf.onRenderHeader.add((survey, canvas) =>
    onRenderHeader(
      survey,
      canvas,
      isLayout,
      nameTitle,
      name,
      birthDateTitle,
      birthDate,
      logoBase64
    )
  );

  if (!isLayout) {
    surveyPdf.onRenderQuestion.add((sender, options) =>
      onRenderQuestion(sender, options, type)
    );
  }

  if (returnAsFile) {
    return await surveyPdf.raw('blob');
  } else {
    surveyPdf.save();
  }
}

function transformQuestion(question: Question, survey: SurveyPDF) {
  const transformedQuestion = QuestionFactory.Instance.createQuestion(
    'text',
    question.name
  );

  const qJSON: Question = question.toJSON();
  qJSON.name = question.name + '_transformed';

  transformedQuestion.fromJSON(qJSON);
  transformedQuestion.setSurveyImpl(survey);
  transformedQuestion.value = question.getDisplayValue(true);
  transformedQuestion.setPropertyValue('no', question.no);

  return transformedQuestion;
}

function onRenderQuestion(sender: SurveyPDF, options: any, type: SurveyPDFType) {
  const starBrick = options.bricks[0]
    .unfold()
    .find((brick: ICustomPdfBrick) => brick.text === '*');
  starBrick?.text && (starBrick.text = '');

  if (type === 'clinic') {
    const textBrick = options.bricks[0].unfold()[1];
    textBrick?.text && (textBrick.text = textBrick.question.printText || textBrick.text);
  }

  if (
    options.question.getType() === 'checkbox' ||
    options.question.getType() === 'radiogroup' ||
    options.question.getType() === 'boolean' ||
    options.question.getType() === 'rating'
  ) {
    const transformedQuestion = transformQuestion(options.question, sender);

    return new Promise<void>((resolve) => {
      SurveyHelper.generateQuestionFlats(
        sender,
        options.controller,
        transformedQuestion,
        SurveyHelper.createPoint(options.bricks[0], true, true)
      ).then((res) => {
        options.bricks = res;
        resolve();
      });
    });
  }
}

function onRenderHeader(
  survey: SurveyPDF,
  canvas: DrawCanvas,
  isLayout: boolean,
  nameTitle: string,
  name: string,
  birthDateTitle: string,
  birthDate: string,
  logoBase64: string | null
) {
  canvas.drawText({
    text: `${nameTitle}: ${isLayout || name === '' ? '' : name}`,
    isBold: true,
    horizontalAlign: HorizontalAlign.Left,
    margins: {
      left: 30,
      bot: 60,
    },
  });

  canvas.drawText({
    text: `${birthDateTitle}: ${
      isLayout || birthDate === '' ? '' : getPersonBirthday(birthDate)
    }`,
    isBold: true,
    horizontalAlign: HorizontalAlign.Left,
    verticalAlign: VerticalAlign.Bottom,
    margins: {
      left: 30,
      bot: 25,
    },
  });

  if (logoBase64) {
    return canvas.drawImage({
      base64: logoBase64,
      horizontalAlign: HorizontalAlign.Right,
      width: (canvas.rect.yBot - canvas.rect.yTop) * 1.5,
      height: (canvas.rect.yBot - canvas.rect.yTop) * 0.5,
      margins: { right: 30 },
    });
  }
}
